import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Lock, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hbmSocialApi } from "@/lib/hbmSocial";
import { HBMSituationWorkspace } from "@/components/HBMSituationWorkspace";
import { HBMSituationDetail } from "@/components/HBMSituationDetail";
import { HBMShareReviewModal } from "@/components/HBMShareReviewModal";
import { HBMResponseTrace } from "@/components/HBMResponseTrace";
import "./HBMSocialWorkspace.css";

function LockedState({ user }) {
  return (
    <main className="hbm-social-locked" data-testid="hbm-social-locked-screen">
      <section>
        <Lock size={34} />
        <span>RESTRICTED ACCESS</span>
        <h1>This workspace is private.</h1>
        <p>{user === false ? "Sign in with an authorized account to open the workspace." : "Your current account does not have access to this workspace."}</p>
        {user === false ? <Link to="/login" data-testid="hbm-social-login-link">Go to login</Link> : <Link to="/" data-testid="hbm-social-return-chat-link">Return to chat</Link>}
      </section>
    </main>
  );
}

const views = {
  overview: "Socials overview",
  private: "Private reasoning",
  situations: "Situation feed",
  insights: "Outcome notes",
  analytics: "Social analytics",
  calibration: "Calibration dashboard",
  communities: "Communities",
  messages: "Messages",
  forecasts: "Forecasts",
  ledger: "Reality Ledger",
  people: "People",
  moderation: "Moderation",
  search: "Search",
  trace: "Response Trace",
};

const tabs = [
  ["overview", "Overview"],
  ["private", "Private reasoning"],
  ["situations", "Feed"],
  ["analytics", "Analytics"],
  ["calibration", "Calibration"],
  ["communities", "Communities"],
  ["messages", "Messages"],
  ["forecasts", "Forecasts"],
  ["ledger", "Ledger"],
  ["people", "People"],
  ["moderation", "Moderation"],
  ["search", "Search"],
  ["insights", "Outcomes"],
  ["trace", "Response Trace"],
];

function displayLabel(value, fallback = "Private reasoning") {
  if (!value) return fallback;
  return String(value).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, (m) => m.toUpperCase());
}

function displayTitle(value) {
  return String(value || "Untitled situation")
    .replace(/^(LIVE_INFERENCE_TEST(?:_[A-Z0-9]+)*|TEST_[A-Z0-9_]+|LATENCY_CHECK_PRIVATE_REASONING)_[a-f0-9]*:?\s*/i, "")
    .replace(/^(LIVE_INFERENCE_TEST(?:_[A-Z0-9]+)*|TEST_[A-Z0-9_]+|LATENCY_CHECK_PRIVATE_REASONING):?\s*/i, "")
    .trim() || "Untitled situation";
}

function fmt(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  return String(value);
}

function pct(value) {
  if (value === null || value === undefined) return "—";
  return `${Math.round(Number(value) * 100)}%`;
}

export default function HBMSocialWorkspace() {
  const { user } = useAuth();
  const [active, setActive] = useState("overview");
  const [status, setStatus] = useState(null);
  const [situations, setSituations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState("");
  const [outcome, setOutcome] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [perspective, setPerspective] = useState("");
  const [perspectiveLens, setPerspectiveLens] = useState("Alternative explanation");
  const [confidence, setConfidence] = useState(65);
  const [shareReview, setShareReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [live, setLive] = useState(null);
  const [profile, setProfile] = useState(null);
  const [people, setPeople] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [messages, setMessages] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [moderationDash, setModerationDash] = useState(null);
  const [providerConfig, setProviderConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [profileDraft, setProfileDraft] = useState({ display_name: "", bio: "", expertise: "" });
  const [communityDraft, setCommunityDraft] = useState({ name: "", description: "", visibility: "private", rules: "" });
  const [messageDraft, setMessageDraft] = useState({ participant_emails: "", body: "" });
  const [forecastDraft, setForecastDraft] = useState({ question: "", resolution_policy: "", visibility: "private", options: "" });
  const [forecastEntryDraft, setForecastEntryDraft] = useState({ question_id: "", probability: "", confidence: 0.65, rationale: "", shared: false });
  const [finalizeDraft, setFinalizeDraft] = useState({ question_id: "", outcome: "", reasoning: "", evidence_url: "" });
  const [providerDraft, setProviderDraft] = useState({ endpoint_url: "", model_name: "", api_key: "", provider: "gonkarouter", enabled: true, reasoning_effort: "low" });

  useEffect(() => { document.title = "HBM Socials"; }, []);

  useEffect(() => {
    if (!user || user === false) return;
    let cancelled = false;
    async function load() {
      try {
        const [nextStatus, list, liveStatus, myProfile, profileList, communityList, messageList, forecastList, ledgerList, reportList, analyticsDash, calibrationDash, moderationAnalytics, providerCfg] = await Promise.all([
          hbmSocialApi.status(),
          hbmSocialApi.listSituations("all"),
          hbmSocialApi.liveStatus(),
          hbmSocialApi.getProfile(),
          hbmSocialApi.listProfiles(),
          hbmSocialApi.listCommunities(),
          hbmSocialApi.listMessages(),
          hbmSocialApi.listForecasts(),
          hbmSocialApi.listRealityLedger(),
          hbmSocialApi.listReports("open"),
          hbmSocialApi.analyticsDashboard(),
          hbmSocialApi.calibrationDashboard(),
          hbmSocialApi.moderationDashboard(),
          hbmSocialApi.getProviderConfig(),
        ]);
        if (!cancelled) {
          setStatus(nextStatus);
          setSituations(list.situations || []);
          setLive(liveStatus);
          setProfile(myProfile.profile || null);
          setProfileDraft({
            display_name: myProfile.profile?.display_name || "",
            bio: myProfile.profile?.bio || "",
            expertise: (myProfile.profile?.expertise || []).join(", "),
          });
          setPeople(profileList.profiles || []);
          setCommunities(communityList.communities || []);
          setMessages(messageList.conversations || []);
          setForecasts(forecastList.forecasts || []);
          setLedger(ledgerList.entries || []);
          setReports(reportList.reports || []);
          setAnalytics(analyticsDash);
          setCalibration(calibrationDash);
          setModerationDash(moderationAnalytics);
          setProviderConfig(providerCfg);
          setProviderDraft({
            endpoint_url: providerCfg.endpoint_url || "",
            model_name: providerCfg.model_name || "",
            api_key: "",
            provider: providerCfg.provider || "gonkarouter",
            enabled: providerCfg.enabled !== false,
            reasoning_effort: providerCfg.reasoning_effort || "low",
          });
          setError("");
        }
      } catch (cause) {
        if (!cancelled) setError(cause?.message || "HBM Social is unavailable.");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  if (user === null) {
    return <main className="hbm-social-loading" data-testid="hbm-social-loading-screen">Loading HBM Social…</main>;
  }
  if (user === false || /super admin/i.test(error) || /SUPER_ADMIN_EMAILS/.test(error)) {
    return <LockedState user={user} />;
  }

  async function refresh(id = selected?.id) {
    const [nextStatus, list, liveStatus, communityList, messageList, forecastList, ledgerList, reportList, profileList, analyticsDash, calibrationDash, moderationAnalytics, providerCfg] = await Promise.all([
      hbmSocialApi.status(), hbmSocialApi.listSituations("all"), hbmSocialApi.liveStatus(), hbmSocialApi.listCommunities(), hbmSocialApi.listMessages(), hbmSocialApi.listForecasts(), hbmSocialApi.listRealityLedger(), hbmSocialApi.listReports("open"), hbmSocialApi.listProfiles(), hbmSocialApi.analyticsDashboard(), hbmSocialApi.calibrationDashboard(), hbmSocialApi.moderationDashboard(), hbmSocialApi.getProviderConfig(),
    ]);
    setStatus(nextStatus);
    setSituations(list.situations || []);
    setLive(liveStatus);
    setCommunities(communityList.communities || []);
    setMessages(messageList.conversations || []);
    setForecasts(forecastList.forecasts || []);
    setLedger(ledgerList.entries || []);
    setReports(reportList.reports || []);
    setPeople(profileList.profiles || []);
    setAnalytics(analyticsDash);
    setCalibration(calibrationDash);
    setModerationDash(moderationAnalytics);
    setProviderConfig(providerCfg);
    setProviderDraft((prev) => ({
      ...prev,
      endpoint_url: providerCfg.endpoint_url || prev.endpoint_url,
      model_name: providerCfg.model_name || prev.model_name,
      provider: providerCfg.provider || prev.provider,
      enabled: providerCfg.enabled !== false,
      reasoning_effort: providerCfg.reasoning_effort || prev.reasoning_effort,
      api_key: "",
    }));
    if (id) {
      const detail = await hbmSocialApi.getSituation(id);
      setSelected(detail.situation);
    }
  }

  async function runAction(label, action) {
    setBusy(true);
    setError("");
    try {
      const result = await action();
      if (label) toast.success(label);
      return result;
    } catch (cause) {
      const msg = cause?.message || "Action failed.";
      setError(msg);
      toast.error(msg);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function handleAnalyze(event) {
    event.preventDefault();
    const body = input.trim();
    if (body.length < 8) return;
    const result = await runAction(null, () => hbmSocialApi.analyze({ body, mode: "dynamic" }));
    if (result?.situation) {
      setSelected(result.situation);
      setActive("private");
      await refresh(result.situation.id);
    }
  }

  async function handleSelectSituation(id) {
    const result = await runAction(null, () => hbmSocialApi.getSituation(id));
    if (result?.situation) setSelected(result.situation);
  }

  async function handleOutcome() {
    if (!selected || outcome.trim().length < 8) return;
    const result = await runAction(null, () => hbmSocialApi.reportOutcome(selected.id, { outcome, clarity: 0.74 }));
    if (result) {
      setOutcome("");
      await refresh(selected.id);
    }
  }

  async function handleContinue(options = {}) {
    if (!selected || followUp.trim().length < 1) return;
    const result = await runAction(null, () => hbmSocialApi.continueSituation(selected.id, { message: followUp.trim(), ...options }));
    if (result?.situation) {
      setSelected(result.situation);
      setFollowUp("");
      await refresh(result.situation.id);
    }
  }

  async function handleForceSynthesize() {
    if (!selected) return;
    const result = await runAction(null, () => hbmSocialApi.forceSynthesize(selected.id));
    if (result?.situation) {
      setSelected(result.situation);
      await refresh(result.situation.id);
    }
  }

  async function handleKeepProbing() {
    if (!selected) return;
    const question = selected.analysis?.investigation?.primary_question || "Ask the next most useful question.";
    const result = await runAction(null, () => hbmSocialApi.continueSituation(selected.id, { message: question, keep_probing: true }));
    if (result?.situation) {
      setSelected(result.situation);
      await refresh(result.situation.id);
    }
  }

  async function handleCloseSituation() {
    if (!selected) return;
    const result = await runAction(null, () => hbmSocialApi.closeSituation(selected.id));
    if (result?.situation) {
      setSelected(result.situation);
      await refresh(result.situation.id);
    }
  }

  async function handleStartShare() {
    if (!selected) return;
    const result = await runAction(null, () => hbmSocialApi.anonymize(selected.id));
    if (result?.situation) {
      setSelected(result.situation);
      setShareReview(true);
    }
  }

  async function handleConfirmShare() {
    if (!selected) return;
    const result = await runAction("Situation shared.", () => hbmSocialApi.publish(selected.id));
    if (result?.situation) {
      setShareReview(false);
      await refresh(result.situation.id);
      setActive("situations");
    }
  }

  async function handlePerspective() {
    if (!selected || perspective.trim().length < 8) return;
    const body = `${perspectiveLens}. Confidence ${confidence}%. ${perspective.trim()}`;
    const result = await runAction(null, () => hbmSocialApi.addPerspective(selected.id, { body }));
    if (result) {
      setPerspective("");
      await refresh(selected.id);
    }
  }

  async function handleSynthesize() {
    if (!selected) return;
    const result = await runAction(null, () => hbmSocialApi.synthesize(selected.id));
    if (result) await refresh(selected.id);
  }

  function handleNew() {
    setSelected(null);
    setInput("");
    setOutcome("");
    setPerspective("");
    setFollowUp("");
    setShareReview(false);
    setActive("private");
  }

  async function handleProfileSave() {
    const result = await runAction("Profile saved.", () => hbmSocialApi.updateProfile({
      display_name: profileDraft.display_name,
      bio: profileDraft.bio,
      expertise: profileDraft.expertise.split(",").map((x) => x.trim()).filter(Boolean),
      discoverable: true,
      activity_public: false,
      reputation_public: false,
    }));
    if (result?.profile) setProfile(result.profile);
    await refresh();
  }

  async function handleProviderSave(event) {
    event.preventDefault();
    if (!providerDraft.endpoint_url.trim() || !providerDraft.model_name.trim()) return;
    const payload = {
      endpoint_url: providerDraft.endpoint_url.trim(),
      model_name: providerDraft.model_name.trim(),
      provider: providerDraft.provider.trim() || "gonkarouter",
      enabled: !!providerDraft.enabled,
      reasoning_effort: providerDraft.reasoning_effort || "low",
      ...(providerDraft.api_key.trim() ? { api_key: providerDraft.api_key.trim() } : {}),
    };
    const result = await runAction("Socials analysis engine saved securely.", () => hbmSocialApi.saveProviderConfig(payload));
    if (result) {
      setProviderConfig(result);
      setProviderDraft((prev) => ({ ...prev, api_key: "" }));
      await refresh();
    }
  }

  async function handleCommunityCreate() {
    if (communityDraft.name.trim().length < 3) return;
    await runAction("Community created.", () => hbmSocialApi.createCommunity({
      ...communityDraft,
      rules: communityDraft.rules.split("\n").map((x) => x.trim()).filter(Boolean),
    }));
    setCommunityDraft({ name: "", description: "", visibility: "private", rules: "" });
    await refresh();
  }

  async function handleMessageSend() {
    if (!messageDraft.body.trim()) return;
    await runAction("Message sent.", () => hbmSocialApi.sendMessage({
      participant_emails: messageDraft.participant_emails.split(",").map((x) => x.trim()).filter(Boolean),
      body: messageDraft.body,
      idempotency_key: `ui-${Date.now()}`,
    }));
    setMessageDraft({ participant_emails: "", body: "" });
    await refresh();
  }

  async function handleForecastCreate() {
    if (forecastDraft.question.trim().length < 8 || forecastDraft.resolution_policy.trim().length < 8) return;
    await runAction("Forecast question created.", () => hbmSocialApi.createForecast({
      ...forecastDraft,
      options: forecastDraft.options.split(",").map((x) => x.trim()).filter(Boolean),
    }));
    setForecastDraft({ question: "", resolution_policy: "", visibility: "private", options: "" });
    await refresh();
  }

  async function handleForecastEntry() {
    if (!forecastEntryDraft.question_id) return;
    await runAction("Forecast recorded.", () => hbmSocialApi.contributeForecast(forecastEntryDraft.question_id, {
      probability: forecastEntryDraft.probability === "" ? null : Number(forecastEntryDraft.probability),
      confidence: Number(forecastEntryDraft.confidence),
      rationale: forecastEntryDraft.rationale,
      shared: !!forecastEntryDraft.shared,
    }));
    setForecastEntryDraft({ question_id: "", probability: "", confidence: 0.65, rationale: "", shared: false });
    await refresh();
  }

  function openFinalizeForecast(id) {
    setFinalizeDraft({ question_id: id, outcome: "", reasoning: "", evidence_url: "" });
  }

  async function handleResolveForecast() {
    if (!finalizeDraft.question_id || !finalizeDraft.outcome.trim() || !finalizeDraft.reasoning.trim()) return;
    await runAction("Forecast finalized into Reality Ledger.", () => hbmSocialApi.resolveForecast(finalizeDraft.question_id, {
      outcome: finalizeDraft.outcome,
      reasoning: finalizeDraft.reasoning,
      evidence_url: finalizeDraft.evidence_url || null,
      finalize_now: true,
    }));
    setFinalizeDraft({ question_id: "", outcome: "", reasoning: "", evidence_url: "" });
    await refresh();
  }

  async function handleSearch(event) {
    event.preventDefault();
    const result = await runAction(null, () => hbmSocialApi.socialSearch(searchQuery));
    if (result) setSearchResults(result.results);
  }

  return (
    <main className="hbm-social-shell" data-testid="hbm-social-workspace">
      <section className="hbm-social-canvas" data-testid="hbm-social-canvas">
        <header className="hbm-social-topbar" data-testid="hbm-social-topbar">
          <div>
            <span>HUMANE BEHAVIOR MODEL</span>
            <h1>{views[active] || "Human Behavioral Intelligence Network"}</h1>
          </div>
          <nav className="hbm-social-tabs" aria-label="HBM workspace sections" data-testid="hbm-social-top-tabs">
            {tabs.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={active === id ? "active" : ""}
                onClick={() => setActive(id)}
                data-testid={`hbm-social-top-tab-${id}`}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="hbm-social-top-actions">
            <span className="hbm-social-live-pill" data-testid="hbm-social-live-feature-count">{live?.features?.length ? `${live.features.length} live modules` : "Loading modules"}</span>
            <button type="button" onClick={handleNew} disabled={busy} data-testid="hbm-social-new-situation-button">New</button>
            <button type="button" onClick={() => refresh()} disabled={busy} data-testid="hbm-social-refresh-button"><RefreshCcw size={15} /> Refresh</button>
          </div>
        </header>
        {error && !/super admin/i.test(error) && <div className="hbm-social-error" data-testid="hbm-social-error-alert"><AlertTriangle size={16} /> {error}</div>}
        <div className="hbm-social-focused-layout">
          {active === "private" && (
            <HBMSituationWorkspace
              input={input}
              setInput={setInput}
              busy={busy}
              situation={selected}
              onAnalyze={handleAnalyze}
              onShare={handleStartShare}
              followUp={followUp}
              setFollowUp={setFollowUp}
              onContinue={handleContinue}
              onForceSynthesize={handleForceSynthesize}
              onKeepProbing={handleKeepProbing}
              onCloseSituation={handleCloseSituation}
            />
          )}

          {active === "overview" && (
            <section className="hbm-social-overview-shell" data-testid="hbm-social-overview-panel">
              <div className="hbm-social-hero-panel" data-testid="socials-overview-container">
                <div>
                  <span>SUPER-ADMIN SOCIAL INTELLIGENCE</span>
                  <h2>One control room for reasoning, community signal, forecasts and moderation.</h2>
                  <p>Start with a private HBM read, publish only after anonymization review, and keep calibration visible as outcomes arrive.</p>
                </div>
                <button type="button" onClick={() => setActive("private")} data-testid="overview-quick-analyze-btn">Analyze issue</button>
              </div>
              <div className="hbm-social-kpi-grid" data-testid="social-overview-kpis">
                <article data-testid="stat-card-live-situations"><span>Live situations</span><b>{fmt(situations.length)}</b><small>{fmt(live?.features?.length)} live modules</small></article>
                <article data-testid="stat-card-inference-accuracy"><span>Calibration inputs</span><b>{fmt(analytics?.counts?.forecast_entries)}</b><small>forecast entries feeding calibration</small></article>
                <article data-testid="stat-card-calibration-score"><span>Brier score</span><b>{fmt(calibration?.brier_score)}</b><small>{calibration?.display ? "sample floor met" : "directional sample"}</small></article>
                <article><span>Moderation</span><b>{fmt(analytics?.counts?.reports_open)}</b><small>{fmt(moderationDash?.messages?.flagged)} flagged messages</small></article>
              </div>
              <div className="hbm-social-split-grid">
                <section data-testid="social-overview-health"><h3>Health signals</h3>{(analytics?.health || []).map((h) => <p key={h.signal}><b>{h.signal}</b><span>{displayLabel(h.level)} · {fmt(h.value)} · {h.detail}</span></p>)}</section>
                <section data-testid="social-overview-provider-setup"><h3>Analysis engine setup</h3><p><b>Primary engine</b><span>{providerConfig?.configured ? "Configured" : "Not configured yet"}</span></p><p><b>Saved secret</b><span>{providerConfig?.configured ? `Stored securely · ${providerConfig.key_masks?.join(", ")}` : "Add key and URL below"}</span></p><p><b>HBM instruction layer</b><span>{status?.inference?.hbm_prompt_applied ? "Active" : "Not active"}</span></p></section>
              </div>
              <form className="hbm-provider-config-form" onSubmit={handleProviderSave} data-testid="social-provider-config-form">
                <div>
                  <span>SECURE ANALYSIS ENGINE SETUP</span>
                  <h3>Save router URL, model and key securely</h3>
                  <p>The key is encrypted into runtime engine storage and never displayed again. Leave the key field blank to keep the existing saved key.</p>
                </div>
                <label>
                  <span>Engine</span>
                  <input value={providerDraft.provider} onChange={(e) => setProviderDraft({ ...providerDraft, provider: e.target.value })} placeholder="gonkarouter" data-testid="social-provider-name-input" />
                </label>
                <label>
                  <span>Endpoint URL</span>
                  <input value={providerDraft.endpoint_url} onChange={(e) => setProviderDraft({ ...providerDraft, endpoint_url: e.target.value })} placeholder="https://.../v1/chat/completions" data-testid="social-provider-endpoint-input" />
                </label>
                <label>
                  <span>Model name</span>
                  <input value={providerDraft.model_name} onChange={(e) => setProviderDraft({ ...providerDraft, model_name: e.target.value })} placeholder="provider/model-name" data-testid="social-provider-model-input" />
                </label>
                <label>
                  <span>API key</span>
                  <input type="password" autoComplete="new-password" value={providerDraft.api_key} onChange={(e) => setProviderDraft({ ...providerDraft, api_key: e.target.value })} placeholder={providerConfig?.configured ? "Saved — enter only to replace" : "Paste API key"} data-testid="social-provider-key-input" />
                </label>
                <label>
                  <span>Reasoning effort</span>
                  <select value={providerDraft.reasoning_effort} onChange={(e) => setProviderDraft({ ...providerDraft, reasoning_effort: e.target.value })} data-testid="social-provider-reasoning-select"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
                </label>
                <label className="hbm-provider-toggle">
                  <input type="checkbox" checked={providerDraft.enabled} onChange={(e) => setProviderDraft({ ...providerDraft, enabled: e.target.checked })} data-testid="social-provider-enabled-checkbox" />
                  <span>Enable as primary Socials analysis engine</span>
                </label>
                <button type="submit" disabled={busy || !providerDraft.endpoint_url.trim() || !providerDraft.model_name.trim()} data-testid="social-provider-save-button">Save secure engine setup</button>
              </form>
            </section>
          )}

          {active === "analytics" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-analytics-panel">
              <header><span>SOCIAL ANALYTICS</span><b>Activity + health</b></header>
              <div className="hbm-social-kpi-grid">
                {Object.entries(analytics?.counts || {}).map(([key, value]) => <article key={key} data-testid="analytics-kpi-card"><span>{displayLabel(key)}</span><b>{fmt(value)}</b></article>)}
              </div>
              <div className="hbm-social-split-grid">
                <section data-testid="analytics-trend-list"><h3>14-day activity trend</h3>{(analytics?.activity_trend || []).map((row) => <p key={row.day}><b>{row.day}</b><span>{fmt(row.messages)} messages · {fmt(row.forecasts)} forecasts · {fmt(row.reports)} reports</span></p>)}</section>
                <section data-testid="analytics-top-communities"><h3>Top communities</h3>{(analytics?.top_communities || []).map((c) => <p key={c.id}><b>{c.name}</b><span>{fmt(c.member_count || 0)} members · {displayLabel(c.visibility)}</span></p>)}</section>
              </div>
            </section>
          )}

          {active === "calibration" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-calibration-panel">
              <header><span>CALIBRATION</span><b>{calibration?.display ? "Sample floor met" : "Directional"}</b></header>
              <div className="hbm-social-kpi-grid">
                <article><span>Resolved questions</span><b>{fmt(calibration?.resolved_questions)}</b><small>floor {fmt(calibration?.sample_floor)}</small></article>
                <article><span>Scored entries</span><b>{fmt(calibration?.scored_entries)}</b><small>{fmt(calibration?.forecast_entries)} total entries</small></article>
                <article><span>Brier score</span><b>{fmt(calibration?.brier_score)}</b><small>lower is better</small></article>
                <article><span>Directional accuracy</span><b>{pct(calibration?.accuracy)}</b><small>{calibration?.message}</small></article>
              </div>
              <div className="hbm-social-calibration-bars" data-testid="calibration-bucket-chart">
                {(calibration?.buckets || []).map((b) => <div key={b.bucket}><span>{b.bucket}</span><i style={{ width: `${Math.max(4, (b.count || 0) * 18)}px` }} /><b>{fmt(b.count)} · pred {fmt(b.avg_prediction)} · obs {fmt(b.observed_frequency)}</b></div>)}
              </div>
              <div className="hbm-social-live-grid">{(calibration?.recent_resolutions || []).map((r) => <article key={r.id} data-testid="calibration-resolution-card"><b>{r.question}</b><span>{r.outcome} · {fmt(r.entry_count)} entries</span><p>{r.resolved_at}</p></article>)}</div>
            </section>
          )}

          {active === "situations" && (
            <div className="hbm-feed-layout" data-testid="situation-feed-container">
              <section className="hbm-social-card hbm-social-feed" data-testid="hbm-social-feed-panel">
                <header><span>SITUATIONS</span><b>{situations.length ? `${situations.length} saved` : "Empty"}</b></header>
                <input className="hbm-feed-search" placeholder="Search situations…" data-testid="situation-feed-search-input" />
                <select className="hbm-feed-filter" data-testid="situation-feed-filter-select" defaultValue="all">
                  <option value="all">All</option>
                  <option value="published">Shared</option>
                  <option value="private">Private</option>
                  <option value="unresolved">Unresolved</option>
                </select>
                <div>
                  {situations.slice(0, 12).map((item) => (
                    <button type="button" key={item.id} className={selected?.id === item.id ? "active" : ""} onClick={() => handleSelectSituation(item.id)} data-testid="situation-feed-item-card">
                      <b>{displayTitle(item.title)}</b>
                      <span>{displayLabel(item.analysis?.domain)}</span>
                    </button>
                  ))}
                  {!situations.length && <p>No situations yet.</p>}
                </div>
              </section>
              {selected && (
                <HBMSituationDetail
                  situation={selected}
                  perspective={perspective}
                  setPerspective={setPerspective}
                  perspectiveLens={perspectiveLens}
                  setPerspectiveLens={setPerspectiveLens}
                  confidence={confidence}
                  setConfidence={setConfidence}
                  outcome={outcome}
                  setOutcome={setOutcome}
                  busy={busy}
                  onAddPerspective={handlePerspective}
                  onSynthesize={handleSynthesize}
                  onOutcome={handleOutcome}
                />
              )}
            </div>
          )}

          {active === "insights" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-insights-panel">
              <header><span>OUTCOME NOTES</span><b>Private log</b></header>
              <p>Outcome notes are saved from each situation after you record what happened later.</p>
            </section>
          )}

          {active === "communities" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-communities-panel">
              <header><span>COMMUNITIES</span><b>{communities.length} live</b></header>
              <div className="hbm-social-form-grid">
                <input value={communityDraft.name} onChange={(e) => setCommunityDraft({ ...communityDraft, name: e.target.value })} placeholder="Community name" data-testid="community-name-input" />
                <select value={communityDraft.visibility} onChange={(e) => setCommunityDraft({ ...communityDraft, visibility: e.target.value })} data-testid="community-visibility-select"><option value="private">Private</option><option value="public">Public</option></select>
                <textarea value={communityDraft.description} onChange={(e) => setCommunityDraft({ ...communityDraft, description: e.target.value })} placeholder="Description" data-testid="community-description-input" />
                <textarea value={communityDraft.rules} onChange={(e) => setCommunityDraft({ ...communityDraft, rules: e.target.value })} placeholder="Rules, one per line" data-testid="community-rules-input" />
                <button type="button" onClick={handleCommunityCreate} disabled={busy} data-testid="community-create-button">Create community</button>
              </div>
              <div className="hbm-social-live-grid">{communities.map((c) => <article key={c.id} data-testid="community-card"><b>{c.name}</b><span>{displayLabel(c.visibility)} · {c.member_count || 0} members</span><p>{c.description}</p><button type="button" onClick={() => hbmSocialApi.createCommunityInvite(c.id).then((r) => toast.success(`Invite: ${r.invite_token}`))} data-testid="community-invite-button">Create invite</button></article>)}</div>
            </section>
          )}

          {active === "messages" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-messages-panel">
              <header><span>MESSAGES</span><b>{messages.length} threads</b></header>
              <div className="hbm-social-form-grid">
                <input value={messageDraft.participant_emails} onChange={(e) => setMessageDraft({ ...messageDraft, participant_emails: e.target.value })} placeholder="Participant emails, comma separated" data-testid="message-participants-input" />
                <textarea value={messageDraft.body} onChange={(e) => setMessageDraft({ ...messageDraft, body: e.target.value })} placeholder="Message" data-testid="message-body-input" />
                <button type="button" onClick={handleMessageSend} disabled={busy} data-testid="message-send-button">Send message</button>
              </div>
              <div className="hbm-social-live-grid">{messages.map((m) => <article key={m.id} data-testid="message-thread-card"><b>{(m.participant_emails || []).join(", ") || m.id}</b><p>{m.last_message}</p><span>{m.updated_at}</span></article>)}</div>
            </section>
          )}

          {active === "forecasts" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-forecasts-panel">
              <header><span>FORECASTS</span><b>{forecasts.length} questions</b></header>
              <div className="hbm-social-form-grid">
                <input value={forecastDraft.question} onChange={(e) => setForecastDraft({ ...forecastDraft, question: e.target.value })} placeholder="Forecast question" data-testid="forecast-question-input" />
                <input value={forecastDraft.resolution_policy} onChange={(e) => setForecastDraft({ ...forecastDraft, resolution_policy: e.target.value })} placeholder="Resolution policy" data-testid="forecast-policy-input" />
                <input value={forecastDraft.options} onChange={(e) => setForecastDraft({ ...forecastDraft, options: e.target.value })} placeholder="Options, comma separated" data-testid="forecast-options-input" />
                <select value={forecastDraft.visibility} onChange={(e) => setForecastDraft({ ...forecastDraft, visibility: e.target.value })} data-testid="forecast-visibility-select"><option value="private">Private</option><option value="public">Public</option></select>
                <button type="button" onClick={handleForecastCreate} disabled={busy} data-testid="forecast-create-button">Create forecast</button>
              </div>
              <div className="hbm-social-form-grid compact">
                <select value={forecastEntryDraft.question_id} onChange={(e) => setForecastEntryDraft({ ...forecastEntryDraft, question_id: e.target.value })} data-testid="forecast-entry-question-select"><option value="">Select question</option>{forecasts.map((f) => <option key={f.id} value={f.id}>{f.question}</option>)}</select>
                <input type="number" min="0" max="1" step="0.01" value={forecastEntryDraft.probability} onChange={(e) => setForecastEntryDraft({ ...forecastEntryDraft, probability: e.target.value })} placeholder="Probability 0-1" data-testid="forecast-entry-probability-input" />
                <textarea value={forecastEntryDraft.rationale} onChange={(e) => setForecastEntryDraft({ ...forecastEntryDraft, rationale: e.target.value })} placeholder="Rationale" data-testid="forecast-entry-rationale-input" />
                <button type="button" onClick={handleForecastEntry} disabled={busy} data-testid="forecast-entry-submit-button">Record forecast</button>
              </div>
              <div className="hbm-social-resolution-box" data-testid="forecast-finalize-panel">
                <b>Finalize forecast</b>
                <select value={finalizeDraft.question_id} onChange={(e) => setFinalizeDraft({ ...finalizeDraft, question_id: e.target.value })} data-testid="forecast-finalize-question-select"><option value="">Select forecast</option>{forecasts.map((f) => <option key={f.id} value={f.id}>{f.question}</option>)}</select>
                <input value={finalizeDraft.outcome} onChange={(e) => setFinalizeDraft({ ...finalizeDraft, outcome: e.target.value })} placeholder="Outcome / resolution result" data-testid="forecast-finalize-outcome-input" />
                <input value={finalizeDraft.evidence_url} onChange={(e) => setFinalizeDraft({ ...finalizeDraft, evidence_url: e.target.value })} placeholder="Evidence URL, optional" data-testid="forecast-finalize-evidence-input" />
                <textarea value={finalizeDraft.reasoning} onChange={(e) => setFinalizeDraft({ ...finalizeDraft, reasoning: e.target.value })} placeholder="Evidence-backed resolution reasoning" data-testid="forecast-finalize-reasoning-input" />
                <div>
                  <button type="button" onClick={handleResolveForecast} disabled={busy || !finalizeDraft.question_id || !finalizeDraft.outcome.trim() || !finalizeDraft.reasoning.trim()} data-testid="forecast-finalize-submit-button">Finalize to Reality Ledger</button>
                  <button type="button" onClick={() => setFinalizeDraft({ question_id: "", outcome: "", reasoning: "", evidence_url: "" })} data-testid="forecast-finalize-cancel-button">Clear</button>
                </div>
              </div>
              <div className="hbm-social-live-grid">{forecasts.map((f) => <article key={f.id} data-testid="forecast-card"><b>{f.question}</b><span>{displayLabel(f.status)} · {f.entry_count || 0} entries</span><p>{f.resolution_policy}</p><button type="button" onClick={() => openFinalizeForecast(f.id)} data-testid="forecast-finalize-button">Finalize to ledger</button></article>)}</div>
            </section>
          )}

          {active === "ledger" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-ledger-panel"><header><span>REALITY LEDGER</span><b>{ledger.length} resolved</b></header><div className="hbm-social-live-grid">{ledger.map((l) => <article key={l.id} data-testid="ledger-entry-card"><b>{l.question}</b><span>{l.outcome}</span><p>{l.reasoning}</p></article>)}</div></section>
          )}

          {active === "people" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-people-panel">
              <header><span>PEOPLE</span><b>{people.length} profiles</b></header>
              <div className="hbm-social-form-grid">
                <input value={profileDraft.display_name} onChange={(e) => setProfileDraft({ ...profileDraft, display_name: e.target.value })} placeholder="Display name" data-testid="profile-display-name-input" />
                <input value={profileDraft.expertise} onChange={(e) => setProfileDraft({ ...profileDraft, expertise: e.target.value })} placeholder="Expertise, comma separated" data-testid="profile-expertise-input" />
                <textarea value={profileDraft.bio} onChange={(e) => setProfileDraft({ ...profileDraft, bio: e.target.value })} placeholder="Bio" data-testid="profile-bio-input" />
                <button type="button" onClick={handleProfileSave} disabled={busy} data-testid="profile-save-button">Save profile</button>
              </div>
              {profile && <p className="hbm-social-muted" data-testid="active-profile-summary">Signed in as {profile.display_name || profile.email}</p>}
              <div className="hbm-social-live-grid">{people.map((p) => <article key={p.id || p.user_id} data-testid="people-profile-card"><b>{p.display_name || p.email}</b><span>{(p.expertise || []).join(", ")}</span><p>{p.bio}</p></article>)}</div>
            </section>
          )}

          {active === "moderation" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-moderation-panel">
              <header><span>MODERATION</span><b>{reports.length} open</b></header>
              <div className="hbm-social-kpi-grid" data-testid="moderation-dashboard-kpis">
                <article><span>Total reports</span><b>{fmt(moderationDash?.reports?.total)}</b><small>open {fmt(moderationDash?.reports?.by_status?.open)}</small></article>
                <article><span>Flagged messages</span><b>{fmt(moderationDash?.messages?.flagged)}</b><small>{fmt(moderationDash?.messages?.scanned)} scanned</small></article>
                <article><span>Flagged communities</span><b>{fmt(moderationDash?.communities?.flagged)}</b><small>{fmt(moderationDash?.communities?.scanned)} scanned</small></article>
                <article><span>Join requests</span><b>{fmt(moderationDash?.communities?.join_requests?.length || 0)}</b><small>community approvals</small></article>
              </div>
              <div className="hbm-social-split-grid">
                <section data-testid="moderation-message-queue"><h3>Message queue</h3>{(moderationDash?.messages?.queue || []).map((m) => <p key={m.id}><b>{displayLabel(m.severity)} · {m.sender_email}</b><span>{(m.flags || []).join(", ")} · {m.body}</span></p>)}</section>
                <section data-testid="moderation-community-queue"><h3>Community queue</h3>{(moderationDash?.communities?.queue || []).map((c) => <p key={c.id}><b>{c.name}</b><span>{(c.flags || []).join(", ")} · {displayLabel(c.visibility)}</span></p>)}</section>
              </div>
              <div className="hbm-social-live-grid">{reports.map((r) => <article key={r.id} data-testid="moderation-report-card"><b>{r.target_type}: {r.target_id}</b><p>{r.reason}</p><button type="button" onClick={() => hbmSocialApi.resolveReport(r.id, { status: "resolved", note: "Resolved from super-admin workspace" }).then(() => refresh())} data-testid="moderation-resolve-button">Resolve</button></article>)}</div>
            </section>
          )}

          {active === "search" && (
            <section className="hbm-social-card hbm-social-wide-card" data-testid="hbm-social-search-panel">
              <header><span>SEARCH</span><b>Federated</b></header>
              <form onSubmit={handleSearch} className="hbm-social-search-row"><input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search Situations, Communities, Forecasts and Ledger" data-testid="social-search-input" /><button type="submit" disabled={busy} data-testid="social-search-submit-button">Search</button></form>
              {searchResults && <div className="hbm-social-live-grid" data-testid="social-search-results">{Object.entries(searchResults).map(([section, rows]) => <article key={section}><b>{displayLabel(section)}</b><span>{rows.length} results</span>{rows.slice(0, 4).map((row) => <p key={row.id}>{row.title || row.name || row.question || row.outcome || row.id}</p>)}</article>)}</div>}
            </section>
          )}

          {active === "trace" && <HBMResponseTrace />}
        </div>
      </section>
      <HBMShareReviewModal open={shareReview} situation={selected} busy={busy} onClose={() => setShareReview(false)} onConfirm={handleConfirmShare} />
    </main>
  );
}